import { type ColumnMeta, type RowData } from "@tanstack/react-table";
import type { ViewTableFeatures } from "./table";
import { z } from "zod";
import { getSavedVariantCandidates } from "@helpers/viewColumnVariant";

const VariantMetaSchema = z.object({
    customVariants: z.record(z.string(), z.string()).nullish().catch(undefined),
    variant: z.string().nullish().catch(undefined),
});

export const resolveColumnVariant = <
    TVariant extends string,
    TData extends RowData,
    TValue,
>({
    meta,
    schema,
    fallback,
    optionKey = "variant",
}: {
    meta: ColumnMeta<ViewTableFeatures, TData, TValue> | undefined;
    schema: z.ZodType<TVariant>;
    fallback: TVariant;
    optionKey?: string;
}): TVariant => {
    const parsedMeta = VariantMetaSchema.safeParse(meta);

    if (!parsedMeta.success) {
        return fallback;
    }

    // Candidates are validated independently so an unknown value in one
    // source cannot discard a valid value in the other.
    const candidates = getSavedVariantCandidates({
        customVariants: parsedMeta.data.customVariants,
        legacyVariant: parsedMeta.data.variant,
        optionKey,
    });

    for (const candidate of candidates) {
        const parsedVariant = schema.safeParse(candidate);
        if (parsedVariant.success) {
            return parsedVariant.data;
        }
    }

    return fallback;
};
